/**
 * Deckbiter — Card & Tile Generator
 * =================================
 *
 * A self-contained extraction of the card and board-tile *visual generation*
 * from the original client (`Old Deckbiter/public/index.js`). This is the one
 * piece we want to carry forward into the new client, so it has been lifted out
 * of the old monolith and given a clean, injectable dependency contract.
 *
 * The layout math (every coordinate, font size, colour and frame-selection
 * rule) is preserved exactly as it was in the shipping client — that pixel
 * layout is the valuable part. What changed is only the *plumbing* around it:
 *   - scattered globals (`PIXI`, `p`, `cards`, `arts`, `userData`, `duel`) are
 *     now module state populated by `init()`.
 *   - direct calls into the old app (`Click`, `AddCard`, `RemoveCard`) are
 *     routed through injectable `handlers` so this module has no dependency on
 *     the rest of the client.
 *   - the GCS-upload / IndexedDB-cache batch pipeline was left behind (it is
 *     collection plumbing, not generation). See README for where it lived.
 *
 * ---------------------------------------------------------------------------
 * DEPENDENCY CONTRACT — supply these to `init()`:
 *
 *   PIXI      (required)  pixi.js v8 namespace (the client shipped v8.14.3).
 *   cards     (required)  Array of card definitions. A card looks like:
 *                           { id, name, tags:[], cropCost, meatCost, waitCost,
 *                             cropGain, meatGain, attack, health, creature,
 *                             event, text, flavor, ... }
 *                         (see `Old Deckbiter/cards.js`).
 *   arts      (optional)  Map "<cardId>_0" -> { src } placeholder art used when
 *                         a card has no `selectedArt`. `registerArts()` can fill
 *                         this in for you.
 *   userData  (optional)  Enables the content censor (`CensorCard`). Without it
 *                         censoring is skipped and art is shown as-is.
 *   duel      (optional)  Only read for `trueForm`/egg tiles.
 *   handlers  (optional)  { click(spot), addCard(index), removeCard(index) }
 *                         interaction callbacks for on-stage (non-render) use.
 *
 * Fonts ("Luckiest Guy", "Londrina Solid", "Merriweather") are loaded via the
 * global WebFont loader if present; otherwise load them yourself before render.
 *
 * ---------------------------------------------------------------------------
 * PUBLIC API (see the bottom of this file):
 *   await init(deps)                 -> PIXI.Application ("p")
 *   await PixiCard(card, y, x, mode) -> PIXI.Container   (a full card)
 *   await PixiTile(card, y, x, mode) -> PIXI.Container   (a board tile)
 *   await renderCardToImage(card)    -> HTMLImageElement
 *   await renderTileToImage(card)    -> HTMLImageElement
 *   await DemoPixiCard(...ids)       -> renders a preview overlay in the DOM
 *   registerArts(cards)              -> populate placeholder `arts` entries
 *   CardName(card), SRC(card), RenderId(card) and the TextStyle* helpers
 */
// ESM module (converted from UMD per plan/file-structure.md "card-tile.js
// note"): the new client bundles this via `import`; demo.html loads it with
// <script type="module"> and uses the window.CardTileGenerator global below.

  // ---------------------------------------------------------------------------
  // Module state (populated by init)
  // ---------------------------------------------------------------------------
  let PIXI = null; // pixi.js v8 namespace
  let p = null; // the PIXI.Application (was the global `p`)
  let cards = []; // card definitions (was the global `cards`)
  let arts = {}; // "<id>_0" -> { src } placeholder art (was the global `arts`)
  // selectedArt filename -> in-flight/settled Promise<url>, so concurrent
  // renders of the SAME art (an opening hand with duplicate cards) share ONE
  // fetch instead of racing to blob-cache the same image.
  let artSrcPromises = {};
  let onlineImages = {}; // prerendered-art lookup (was the global `onlineImages`)
  let userData = null; // enables CensorCard when present
  let duel = null; // only read for trueForm/egg tiles

  const handlers = {
    click: (spot) => console.debug("[CardTileGenerator] click:", spot),
    addCard: (index) => console.debug("[CardTileGenerator] addCard:", index),
    removeCard: (index) =>
      console.debug("[CardTileGenerator] removeCard:", index),
  };

  const config = {
    // Base for the shared card/board frame textures.
    frameBase: "https://storage.googleapis.com/deckbiter/frames",
    // Base for per-card art (fetched as "<storageBase>/<selectedArt>" and
    // "<storageBase>/Card_<selectedArt>.png").
    storageBase: "https://storage.googleapis.com/deckbiter",
    // Fallback art when a sprite fails to load.
    fallbackArt: "https://deckbiter.netlify.app/cards/Error.png",
    // Placeholder art used by registerArts() when a card has no real art.
    placeholderArt: "https://deckbiter.netlify.app/cards/Missing.png",
  };

  // ---------------------------------------------------------------------------
  // Constants
  // ---------------------------------------------------------------------------
  const GENDERS = [
    "generic",
    "male",
    "female",
    "futa",
    "gyno",
    "male_x_male",
    "male_x_female",
    "male_x_futa",
    "male_x_gyno",
    "female_x_male",
    "female_x_female",
    "female_x_futa",
    "female_x_gyno",
    "futa_x_male",
    "futa_x_female",
    "futa_x_futa",
    "futa_x_gyno",
    "gyno_x_male",
    "gyno_x_female",
    "gyno_x_futa",
    "gyno_x_gyno",
  ];

  // Per-element vertical nudges applied to a finished card (kept from original).
  const Ymod = { name: -10, tags: 0, text: 15, cost: 0, stat: 0 };

  // Alias -> filename (under config.frameBase) for the shared frame/board art.
  // NOTE: `frameMeatEvent` was referenced by the original PixiCard but never
  // registered there, so meat events rendered without a frame. It is included
  // here so the frame set is complete (see README "Deviations").
  const ASSET_FILES = {
    frameFree: "Frame_Free.png",
    frameFreeEvent: "Frame_Free_Event.png",
    frameCrop: "Frame_Crop.png",
    frameCropEvent: "Frame_Crop_Event.png",
    frameCropSpecial: "Frame_Crop_Special.png",
    frameMeat: "Frame_Meat.png",
    frameMeatEvent: "Frame_Meat_Event.png",
    frameWait: "Frame_Wait.png",
    boardFree: "Board_Free.png",
    boardCrop: "Board_Crop.png",
    boardCropSpecial: "Board_Crop_Special.png",
    boardMeat: "Board_Meat.png",
    boardWait: "Board_Wait.png",
    boardNone: "Board_None.png",
  };

  // ---------------------------------------------------------------------------
  // init
  // ---------------------------------------------------------------------------
  /**
   * Wire up the generator.
   * @param {object} deps
   * @param {object}  deps.PIXI      pixi.js v8 namespace (required).
   * @param {Array}   deps.cards     card definitions (required for id lookups).
   * @param {object} [deps.app]      an existing PIXI.Application to reuse.
   * @param {Element}[deps.mount]    DOM element to mount a new canvas into.
   * @param {object} [deps.arts]     placeholder-art map.
   * @param {object} [deps.userData] enables the censor.
   * @param {object} [deps.duel]     for trueForm tiles.
   * @param {object} [deps.handlers] { click, addCard, removeCard }.
   * @param {object} [deps.config]   overrides for the `config` block above.
   * @param {boolean}[deps.loadFonts=true] load Google fonts via WebFont.
   * @returns {Promise<PIXI.Application>}
   */
  async function init(deps = {}) {
    if (!deps.PIXI) throw new Error("CardTileGenerator.init: PIXI is required.");
    PIXI = deps.PIXI;
    if (deps.cards) cards = deps.cards;
    if (deps.arts) arts = deps.arts;
    if (deps.userData) userData = deps.userData;
    if (deps.duel) duel = deps.duel;
    if (deps.onlineImages) onlineImages = deps.onlineImages;
    if (deps.config) Object.assign(config, deps.config);
    if (deps.handlers) Object.assign(handlers, deps.handlers);

    if (deps.loadFonts !== false) loadFonts();

    // Reuse a provided app, or create a headless-capable one.
    if (deps.app) {
      p = deps.app;
    } else {
      p = new PIXI.Application();
      const hasWindow = typeof window !== "undefined";
      const dpr = hasWindow ? window.devicePixelRatio || 1 : 1;
      const initOptions = {
        antialias: true,
        autoDensity: true,
        resolution: Math.min(dpr, 2),
        background: 0x141119,
        backgroundAlpha: 1,
      };
      if (deps.mount) {
        initOptions.resizeTo = deps.mount;
      } else {
        initOptions.width = 1280;
        initOptions.height = 720;
      }
      await p.init(initOptions);
      if (deps.mount) {
        p.canvas.style.display = "block";
        p.canvas.id = "myPixiCanvas";
        deps.mount.appendChild(p.canvas);
      }
    }

    // These arrays are pushed to when a card/tile is added to the live stage
    // (i.e. mode !== "JustRender"). Initialise them so that path never throws.
    if (!p.cards) p.cards = [];
    if (!p.gui) p.gui = [];

    await registerAssets();
    return p;
  }

  function loadFonts() {
    if (typeof WebFont === "undefined") {
      console.warn(
        "[CardTileGenerator] WebFont not found — load 'Luckiest Guy', " +
          "'Londrina Solid' and 'Merriweather' yourself for correct text.",
      );
      return;
    }
    WebFont.load({
      google: {
        families: ["Luckiest Guy", "Londrina Solid", "Merriweather"],
      },
      active: () => console.log("[CardTileGenerator] Fonts loaded"),
    });
  }

  // Register + preload every frame/board texture alias. Each is loaded
  // individually so one missing file cannot break the rest.
  async function registerAssets() {
    const aliases = Object.keys(ASSET_FILES);
    for (const alias of aliases) {
      PIXI.Assets.add({ alias, src: config.frameBase + "/" + ASSET_FILES[alias] });
    }
    await Promise.all(
      aliases.map((alias) =>
        PIXI.Assets.load(alias).catch((err) =>
          console.warn("[CardTileGenerator] asset load failed:", alias, err),
        ),
      ),
    );
    console.log("[CardTileGenerator] Frame/board textures ready");
  }

  /**
   * Populate placeholder `arts["<id>_0"]` entries for the given cards so that
   * cards without a real `selectedArt` still render (with placeholder art).
   */
  function registerArts(list = cards) {
    for (const card of list) {
      const key = card.id + "_0";
      if (!arts[key]) arts[key] = { src: config.placeholderArt };
    }
    return arts;
  }

  // ---------------------------------------------------------------------------
  // Art resolution
  // ---------------------------------------------------------------------------

  /**
   * Resolve a card's `selectedArt` (choosing one if unset) and return the
   * storage URL for its rendered image. Mirrors the original SRC().
   */
  function SRC(card, ignoreOnlineImages = true, test = false) {
    try {
      if (!card.selectedArt || card.selectedArt == "0") {
        if (card.collectedArts) {
          card.selectedArt =
            card.collectedArts[
              Math.floor(Math.random() * (card.collectedArts.length - 1))
            ];
        } else if (cards[card.id] && cards[card.id].selectedArt) {
          card.selectedArt = cards[card.id].selectedArt;
        } else if (cards[card.id] && cards[card.id].collectedArts) {
          cards[card.id].selectedArt =
            cards[card.id].collectedArts[
              Math.floor(
                Math.random() * (cards[card.id].collectedArts.length - 1),
              )
            ];
          card.selectedArt = cards[card.id].selectedArt;
        } else if (cards[card.id] && cards[card.id].newarts) {
          cards[card.id].newarts.forEach((art) => {
            card.selectedArt = false;
            if (art.generic) card.selectedArt = art.generic;
          });
          if (card.selectedArt == false) {
            const priorityKeys = [
              "generic",
              "male",
              "female",
              "futa",
              "gyno",
              "male_x_male",
              "male_x_female",
              "male_x_futa",
              "male_x_gyno",
              "female_x_male",
              "female_x_female",
              "female_x_futa",
              "female_x_gyno",
              "futa_x_male",
              "futa_x_female",
              "futa_x_futa",
              "futa_x_gyno",
              "gyno_x_male",
              "gyno_x_female",
              "gyno_x_futa",
              "gyno_x_gyno",
            ];
            cards[card.id].newarts.forEach((art) => {
              card.selectedArt = false;
              for (const key of priorityKeys) {
                if (art[key]) {
                  card.selectedArt = art[key];
                  cards[card.id].selectedArt = card.selectedArt;
                  break;
                }
              }
            });
          }
        }
      }
    } catch (error) {
      console.error("[CardTileGenerator] SRC failed for", card, error);
    }
    CensorCard(card);
    if (card.selectedArt && ignoreOnlineImages) {
      return (
        config.storageBase +
        "/Card_" +
        card.selectedArt.replace(".jpg", ".png")
      );
    }
    if (
      onlineImages[card.selectedArt || ""] ||
      onlineImages[(card.selectedArt || "").replace("jpg", "png")]
    ) {
      return (
        config.storageBase +
        "/Card_" +
        card.selectedArt.replace("jpg", "png")
      );
    }
    if (card.selectedArt) {
      return (
        config.storageBase +
        "/Card_" +
        card.selectedArt.replace("jpg", "png")
      );
    }
    if (test) return false;
    return config.frameBase + "/Glitch.png";
  }

  /**
   * Swap a card's art for a safe alternative when the viewer's content filters
   * (userData.settings) hide the selected art's flags/gender. No-ops when
   * `userData` is not supplied.
   */
  function CensorCard(card) {
    if (!card.selectedArt) return;
    if (!userData || !userData.settings) return; // censor is opt-in
    const art = card.selectedArt;
    const artId = Number(card.selectedArt.split("_")[1]);
    const source = cards[card.id];
    if (!source || !source.newarts) return;
    const artStyle = source.newarts.find((item) => item.art_id == artId);
    if (!artStyle) return;

    const settings = userData.settings;
    let censor = false;
    if (artStyle.nude && settings.censor && settings.censor.nude) censor = true;
    if (artStyle.smut && settings.censor && settings.censor.smut) censor = true;
    if (artStyle.gore && settings.censor && settings.censor.gore) censor = true;
    if (artStyle.vore && settings.censor && settings.censor.vore) censor = true;
    for (let gender of GENDERS) {
      gender = gender.replace("_x_", "_");
      if (
        art.slice(0, art.lastIndexOf(".")).split("_").slice(3).join("_") ==
          gender &&
        settings.gender &&
        settings.gender[gender] == false
      ) {
        censor = true;
      }
    }
    if (censor && source.collectedArts) {
      card.selectedArt =
        source.collectedArts[
          Math.floor(Math.random() * source.collectedArts.length)
        ];
    }
  }

  /**
   * Cache key for a rendered card/tile — encodes the art plus any stat changes
   * so buffed/egg variants render distinctly. Mirrors the original RenderId().
   */
  function RenderId(card, type = "card") {
    if (!card) {
      console.error("[CardTileGenerator] RenderId: missing card");
      return type + "undefined";
    }
    if (!card.selectedArt) {
      if (cards[card.id] && cards[card.id].selectedArt) {
        card.selectedArt = cards[card.id].selectedArt;
      } else {
        SRC(card);
        card.selectedArt = cards[card.id] && cards[card.id].selectedArt;
      }
    }
    if (card.trueForm) {
      if (card.trueForm === true || (duel && card.owner != duel.player1.id)) {
        return (
          type +
          "Egg" +
          card.id +
          "_" +
          card.wait +
          card.attack +
          "_" +
          card.health +
          "_" +
          (card.meatGain + card.cropGain) +
          "_" +
          card.selectedArt
        );
      }
      return (
        type +
        "Egg" +
        card.id +
        "_" +
        card.wait +
        "_" +
        card.trueForm.waitCost +
        card.attack +
        "_" +
        card.health +
        "_" +
        (card.meatGain + card.cropGain) +
        "_" +
        card.selectedArt
      );
    }
    const OG = cards[card.id] || card;
    if (
      card.attack !== OG.attack ||
      card.health !== OG.health ||
      card.meatGain !== OG.meatGain ||
      card.cropGain !== OG.cropGain
    ) {
      return (
        type +
        card.id +
        "_" +
        card.attack +
        "_" +
        card.health +
        "_" +
        (card.meatGain + card.cropGain + "_" + card.selectedArt)
      );
    }
    if (card.selectedArt) return type + card.selectedArt;
    return (
      type +
      card.id +
      "_" +
      card.attack +
      "_" +
      card.health +
      "_" +
      (card.meatGain + card.cropGain + "_" + card.selectedArt)
    );
  }

  // Load a card's real art sprite, trying the local cache then storage, and
  // falling back to the error art. Mirrors the original PixiGetArtTexture().
  async function PixiGetArtTexture(card) {
    if (!card.selectedArt) SRC(card);
    const key = card.selectedArt;

    // Resolve the art's URL ONCE per selectedArt (fetch → blob-cache), sharing
    // one in-flight promise across concurrent callers. Fixes two bugs:
    //   • the old cache check gated on `texture.valid`, a PIXI v7 property that
    //     is undefined in v8 — so the cache NEVER hit and every render re-fetched
    //     the art from the bucket;
    //   • duplicate cards dealt at once each fetched + cached the same image,
    //     racing on arts[] and leaking blob URLs.
    if (!artSrcPromises[key]) {
      artSrcPromises[key] = (async () => {
        if (arts[key] && arts[key].src) return arts[key].src; // already blob-cached
        const imageUrl = config.storageBase + "/" + key;
        // CDN offload hook (pixel-neutral): when the new client injects
        // config.artFetch it tries the Cloudflare edge first and falls back to
        // this same bucket URL; otherwise this is a plain fetch. storageBase is
        // unchanged (the bucket stays the origin/fallback). See js/net/cdn.js.
        const response = await (config.artFetch || fetch)(imageUrl);
        if (!response.ok) {
          throw new Error("Failed to fetch image. HTTP status: " + response.status);
        }
        const objectURL = URL.createObjectURL(await response.blob());
        arts[key] = { src: objectURL }; // cache
        return objectURL;
      })().catch((error) => {
        delete artSrcPromises[key]; // clear so a later render can retry
        throw error;
      });
    }

    try {
      return await LoadSpriteAsync(await artSrcPromises[key]);
    } catch (fetchError) {
      console.error(
        "[CardTileGenerator] failed to load selectedArt:",
        card.name,
        key,
        fetchError,
      );
      return await LoadSpriteAsync(config.fallbackArt);
    }
  }

  async function LoadSpriteAsync(src) {
    let texture;
    if (src.startsWith("blob:")) {
      texture = await PIXI.Assets.load({
        src,
        alias: src,
        parser: "loadTextures",
      });
    } else {
      texture = await PIXI.Assets.load(src);
    }
    return new PIXI.Sprite(texture);
  }

  // ---------------------------------------------------------------------------
  // Text
  // ---------------------------------------------------------------------------

  /**
   * Add an auto-fitting text object to `container`.
   * NOTE the argument order from the original: `i` is Y, `o` is X.
   */
  function PixiText(
    container,
    text,
    i,
    o,
    style,
    size,
    colors,
    maxWidth,
    paragraph = false,
    maxHeight = 1000,
  ) {
    text = (text + "").replaceAll("<br>", "\n").replaceAll("<BR>", "\n");
    const temp = new PIXI.Text({ text, style: style(size, colors) });
    if (paragraph) {
      temp.wordWrap = true;
      temp.style.wordWrapWidth = maxWidth;
      while (temp.height > maxHeight && size > 1) {
        size--;
        temp.style.fontSize = size;
        temp.style.lineHeight *= 0.98;
      }
    } else {
      temp.wordWrap = false;
      while ((temp.width > maxWidth || temp.height > maxHeight) && size > 1) {
        size--;
        temp.style.fontSize = size;
      }
    }
    container.addChild(temp);
    temp.y = i;
    temp.x = o;
    return temp;
  }

  function TextStyleName(size = 36, colors) {
    return new PIXI.TextStyle({
      fontFamily: "Londrina Solid",
      fontSize: size,
      fontWeight: "bold",
      fill: colors[3],
      stroke: { color: colors[0], width: 15 },
      wordWrap: true,
      wordWrapWidth: 440,
      lineJoin: "round",
    });
  }
  function TextStyleTags(size = 36, colors) {
    return new PIXI.TextStyle({
      fontFamily: "Londrina Solid",
      fontSize: size,
      fontWeight: "normal",
      fill: colors[3],
      stroke: { color: colors[0], width: 12 },
      wordWrap: true,
      wordWrapWidth: 440,
      lineJoin: "round",
    });
  }
  function TextStyleRules(size = 36, colors) {
    return new PIXI.TextStyle({
      fontFamily: "Merriweather",
      fontSize: size,
      fontWeight: "900",
      fill: colors[0],
      stroke: { color: colors[2], width: 0 },
      lineHeight: size,
      wordWrap: true,
      wordWrapWidth: 400,
      lineJoin: "round",
    });
  }
  function TextStyleFlavor(size = 36, colors) {
    return new PIXI.TextStyle({
      fontFamily: "Merriweather",
      fontSize: size,
      fontWeight: "300",
      fontStyle: "italic",
      fill: colors[0],
      alpha: 0.8,
      stroke: { color: colors[2], width: 0 },
      lineHeight: size,
      wordWrap: true,
      wordWrapWidth: 420,
      lineJoin: "round",
    });
  }
  function TextStyleCost(size = 36, colors) {
    return new PIXI.TextStyle({
      fontFamily: "Luckiest Guy",
      fontSize: size,
      fill: colors[3],
      stroke: { color: colors[1], width: 25 },
      dropShadow: { color: "#000000", blur: 0, angle: Math.PI / 6, distance: 6 },
      wordWrap: true,
      wordWrapWidth: 440,
      lineJoin: "round",
    });
  }
  function TextStyleGain(size = 36, colors) {
    return new PIXI.TextStyle({
      fontFamily: "Luckiest Guy",
      fontSize: size,
      fontWeight: "bold",
      fill: colors[2],
      stroke: { color: colors[1], width: 5 },
      wordWrap: true,
      wordWrapWidth: 440,
      lineJoin: "round",
    });
  }

  // ---------------------------------------------------------------------------
  // Name resolution (gender-specific overrides)
  // ---------------------------------------------------------------------------
  function CardName(card) {
    let newName = card.name;
    if (!card.selectedArt) return card.name;

    let gender;
    try {
      gender = card.selectedArt.split("_")[3].split(".")[0];
    } catch (err) {
      console.error(
        "[CardTileGenerator] CardName: bad selectedArt",
        card.selectedArt,
        card,
      );
      return card.name;
    }
    card.gender = gender; // stashed for pronoun substitution in PixiCard
    if (["female", "futa"].includes(gender)) {
      if (card.id == 94) newName = "Acorn Princess";
    }
    if (["male", "gyno"].includes(gender)) {
      if (card.id == 35) newName = "Denfather";
      if (card.id == 92) newName = "Papa Roost";
      if (card.id == 71) newName = "Whiskered Warlock";
    }
    return newName;
  }

  // ---------------------------------------------------------------------------
  // TILE GENERATION
  // ---------------------------------------------------------------------------
  /**
   * Build a board-tile container for `card`.
   * @param {object} card
   * @param {number} i     Y position on the stage.
   * @param {number} o     X position on the stage.
   * @param {string|false} mode  "JustRender" to build without adding to stage.
   */
  async function PixiTile(card, i, o, mode = false, x = -1, y = -1) {
    const temp = new PIXI.Container();

    // Board texture branches on cropGain first (special) — keep that order.
    let boardAlias;
    let colors;
    if (card.cropGain) {
      boardAlias = "boardCropSpecial";
      colors = ["#344135", "#629364", "#ADC2B7", "#E5FFF1"];
    } else if (card.cropCost) {
      boardAlias = "boardCrop";
      colors = ["#344135", "#629364", "#ADC2B7", "#E5FFF1"];
    } else if (card.meatCost) {
      boardAlias = "boardMeat";
      colors = ["#551E24", "#B14444", "#D696A8", "#FFB5CA"];
    } else if (card.waitCost || card.trueForm) {
      boardAlias = "boardWait";
      colors = ["#6F5131", "#D2B85B", "#E8D7B2", "#FFECC4"];
    } else {
      boardAlias = "boardFree";
      colors = ["#3A3149", "#7A7088", "#BAAFC7", "#EEE0FF"];
    }
    const [color1, color2, color3, color4] = colors;
    const texture = await PIXI.Assets.load(boardAlias);

    let artSprite;
    try {
      if (card.selectedArt) {
        artSprite = await PixiGetArtTexture(card);
      } else {
        artSprite = PIXI.Sprite.from(arts[card.id + "_" + 0].src);
      }
    } catch (error) {
      console.warn("[CardTileGenerator] tile art failed:", error);
      artSprite = PIXI.Sprite.from(config.fallbackArt);
    }
    artSprite.x = 22;
    artSprite.y = 22;
    artSprite.width = 635;
    artSprite.height = 476;
    temp.addChild(artSprite);

    if (card.health == 0) {
      const colorMatrix = new PIXI.ColorMatrixFilter();
      artSprite.filters = [colorMatrix];
      colorMatrix.brightness(0.5); // dim dead creatures
    }

    temp.y = i;
    temp.x = o;

    const sprite = new PIXI.Sprite(texture);
    sprite.alpha = 1;
    temp.addChild(sprite);

    let tempName = CardName(card);
    if (card.trueForm) {
      if (card.trueForm === true || (duel && card.owner != duel.player1.id)) {
        tempName += " (" + card.wait + ")";
      } else {
        tempName += " (" + card.wait + "/" + card.trueForm.waitCost + ")";
      }
    }
    temp.nameText = PixiText(
      temp,
      tempName,
      15,
      35,
      TextStyleName,
      60,
      [color1, color2, color3, color4],
      500,
      false,
      120,
    );

    temp.eventMode = "static";
    temp.cursor = "pointer";
    sprite.on("pointerdown", () => handlers.click("Board_" + i + "_" + o));

    if (card.cropGain + card.meatGain >= 10) {
      temp.gain = PixiText(
        temp,
        card.cropGain + card.meatGain,
        5,
        600,
        TextStyleGain,
        60,
        [color1, color2, color3, color4],
        250,
      );
    } else if (card.cropGain + card.meatGain >= 0) {
      temp.gain = PixiText(
        temp,
        card.cropGain + card.meatGain,
        5,
        610,
        TextStyleGain,
        60,
        [color1, color2, color3, color4],
        250,
      );
    }
    if (card.attack >= 10) {
      temp.attack = PixiText(
        temp,
        card.attack,
        420,
        35,
        TextStyleGain,
        60,
        ["#6A5439", "#9A8578", "#CAB0AF", "#FFD0BF"],
        250,
      );
    } else {
      temp.attack = PixiText(
        temp,
        card.attack,
        420,
        25,
        TextStyleGain,
        60,
        ["#6A5439", "#9A8578", "#CAB0AF", "#FFD0BF"],
        250,
      );
    }
    if (card.health >= 10) {
      temp.health = PixiText(
        temp,
        card.health,
        420,
        600,
        TextStyleGain,
        60,
        ["#6A4359", "#9A8098", "#CAB0C7", "#FFD0FF"],
        250,
      );
    } else {
      temp.health = PixiText(
        temp,
        card.health,
        420,
        610,
        TextStyleGain,
        60,
        ["#6A4359", "#9A8098", "#CAB0C7", "#FFD0FF"],
        250,
      );
    }

    if (mode != "JustRender") {
      p.stage.addChild(temp);
      p.cards.push(temp);
    }
    return temp;
  }

  // ---------------------------------------------------------------------------
  // CARD GENERATION
  // ---------------------------------------------------------------------------
  /**
   * Build a full card container for `card`.
   * @param {object} card
   * @param {number} i     Y position on the stage.
   * @param {number} o     X position on the stage.
   * @param {string|false} mode  "JustRender" | "AddCard" | "RemoveCard" | "InHand".
   * @param {number} index index passed to add/remove/click handlers.
   */
  async function PixiCard(card, i, o, mode = false, index = -1) {
    const temp = new PIXI.Container();

    let texture;
    let color1 = "#FF00DC";
    let color2 = "#FF00DC";
    let color3 = "#FF00DC";
    let color4 = "#FF00DC";
    if (card.cropCost) {
      if (card.event) texture = PIXI.Assets.get("frameCropEvent");
      else if (card.cropGain) texture = PIXI.Assets.get("frameCropSpecial");
      else texture = PIXI.Assets.get("frameCrop");
      color1 = "#344135";
      color2 = "#629364";
      color3 = "#ADC2B7";
      color4 = "#E5FFF1";
    } else if (card.meatCost) {
      if (card.event) texture = PIXI.Assets.get("frameMeatEvent");
      else texture = PIXI.Assets.get("frameMeat");
      color1 = "#551E24";
      color2 = "#B14444";
      color3 = "#D696A8";
      color4 = "#FFB5CA";
    } else if (card.waitCost || card.trueForm) {
      texture = PIXI.Assets.get("frameWait");
      color1 = "#6F5131";
      color2 = "#D2B85B";
      color3 = "#E8D7B2";
      color4 = "#FFECC4";
    } else {
      if (card.event) texture = PIXI.Assets.get("frameFreeEvent");
      else texture = PIXI.Assets.get("frameFree");
      color1 = "#3A3149";
      color2 = "#7A7088";
      color3 = "#BAAFC7";
      color4 = "#EEE0FF";
    }

    let artSprite;
    try {
      if (card.selectedArt) {
        artSprite = await PixiGetArtTexture(card);
      } else {
        artSprite = PIXI.Sprite.from(arts[card.id + "_" + 0].src);
      }
    } catch (error) {
      console.warn("[CardTileGenerator] card art failed:", error);
      artSprite = PIXI.Sprite.from(config.fallbackArt);
    }
    artSprite.x = 48;
    artSprite.y = 148;
    artSprite.width = 682;
    artSprite.height = 512;
    temp.addChild(artSprite);

    temp.y = i;
    temp.x = o;
    const sprite = new PIXI.Sprite(texture);
    temp.addChild(sprite);

    if (mode == "AddCard") {
      sprite.eventMode = "static";
      sprite.cursor = "pointer";
      sprite.on("pointerdown", () => handlers.addCard(index));
    }
    if (mode == "RemoveCard") {
      sprite.eventMode = "static";
      sprite.cursor = "pointer";
      sprite.on("pointerdown", () => handlers.removeCard(index));
    }
    if (mode == "InHand") {
      temp.eventMode = "static";
      temp.cursor = "pointer";
      sprite.on("pointerdown", () => handlers.click("Card_" + index));
    }

    const cardName = CardName(card);
    temp.nameText = PixiText(
      temp,
      cardName,
      52,
      245,
      TextStyleName,
      86,
      [color1, color2, color3, color4],
      470,
      false,
      120,
    );
    temp.nameText.y += (114 - temp.nameText.height) / 4;

    if (card.cropCost + card.meatCost + card.waitCost >= 10) {
      temp.cost = PixiText(
        temp,
        card.cropCost + card.meatCost + card.waitCost,
        35,
        65,
        TextStyleCost,
        90,
        [color1, color2, color3, color4],
        250,
      );
    } else if (card.cropCost + card.meatCost + card.waitCost) {
      temp.cost = PixiText(
        temp,
        card.cropCost + card.meatCost + card.waitCost,
        -5,
        65,
        TextStyleCost,
        140,
        [color1, color2, color3, color4],
        250,
      );
    } else {
      temp.cost = PixiText(
        temp,
        "∅",
        -5,
        65,
        TextStyleCost,
        140,
        [color1, color2, color3, color4],
        250,
      );
    }

    temp.tags = PixiText(
      temp,
      card.tags.join(" "),
      665,
      40,
      TextStyleTags,
      60,
      [color1, color2, color3, color4],
      700,
      false,
      110,
    );
    temp.tags.x = 382 - temp.tags.width / 2;

    if (card.creature) {
      const colorArray = [color1, color2, color3, color4];
      const numSize = 60;
      if (card.cropGain + card.meatGain >= 10) {
        temp.gain = PixiText(
          temp,
          card.cropGain + card.meatGain,
          132,
          640,
          TextStyleGain,
          numSize * 0.8,
          colorArray,
          250,
        );
      } else if (card.cropGain + card.meatGain >= 0) {
        temp.gain = PixiText(
          temp,
          card.cropGain + card.meatGain,
          132,
          660,
          TextStyleGain,
          numSize,
          colorArray,
          250,
        );
      }
      if (card.attack >= 10) {
        temp.gain = PixiText(
          temp,
          card.attack,
          940,
          45,
          TextStyleGain,
          numSize,
          [color1, color2, color3, color4],
          250,
        );
      } else {
        temp.gain = PixiText(
          temp,
          card.attack,
          940,
          70,
          TextStyleGain,
          numSize,
          [color1, color2, color3, color4],
          250,
        );
      }
      if (card.health >= 10) {
        temp.gain = PixiText(
          temp,
          card.health,
          940,
          635,
          TextStyleGain,
          numSize,
          [color1, color2, color3, color4],
          250,
        );
      } else {
        temp.gain = PixiText(
          temp,
          card.health,
          940,
          660,
          TextStyleGain,
          numSize,
          [color1, color2, color3, color4],
          250,
        );
      }
    }

    let cardText = card.text
      .replaceAll("<br>", "\n")
      .replaceAll(card.name, cardName);
    if (card.gender) {
      if (["gyno", "male"].includes(card.gender)) {
        cardText = cardText.replaceAll("CTHEY", "He");
        cardText = cardText.replaceAll("THEY", "he");
        cardText = cardText.replaceAll("THEIR", "his");
        cardText = cardText.replaceAll("THEM", "him");
      } else if (["female", "futa"].includes(card.gender)) {
        cardText = cardText.replaceAll("CTHEY", "She");
        cardText = cardText.replaceAll("THEY", "she");
        cardText = cardText.replaceAll("THEIR", "her");
        cardText = cardText.replaceAll("THEM", "her");
      }
    }
    cardText = cardText.replaceAll("CTHEY", "It");
    cardText = cardText.replaceAll("THEY", "it");
    cardText = cardText.replaceAll("THEIR", "its");
    cardText = cardText.replaceAll("THEM", "it");

    temp.text = PixiText(
      temp,
      cardText,
      720,
      65,
      TextStyleRules,
      50,
      [color1, color2, color3, color4],
      650,
      true,
      300,
    );

    let creditText = "seekgroove";
    if (card.selectedArt && card.selectedArt.split("_").length >= 3) {
      creditText = card.selectedArt.split("_")[2];
      if (creditText == "Progressive") creditText = "MinaHyena";
      if (creditText == "Draw Panther") creditText = "DrawPanther";
      if (creditText == "Kasia") creditText = "Virlandil";
      if (
        creditText.toLowerCase() == "st tail" ||
        creditText.toLowerCase() == "sttail"
      ) {
        creditText = "SaintxTail";
      }
    }
    temp.credit = PixiText(
      temp,
      creditText,
      900,
      245,
      TextStyleRules,
      86,
      [color1, color2, color3, color4],
      470,
      false,
      120,
    );
    temp.credit.y += (300 - temp.credit.height) / 4;
    temp.credit.alpha = 0.5;
    temp.credit.style.fontSize = Math.ceil(temp.text.style.fontSize * 0.85);
    temp.credit.x = 382 - temp.credit.width / 2;

    if (temp.text.width < 600 && !card.text.includes("<br>")) {
      temp.text.x = 382 - temp.text.width / 2;
      temp.text.y += 60;
    }

    // Per-element vertical nudges.
    temp.nameText.y += Ymod.name;
    temp.tags.y += Ymod.tags;
    temp.text.y += Ymod.text;
    temp.cost.y += Ymod.cost;
    // NOTE: the original also ran `card.attack.y += Ymod.stat`, but `card.attack`
    // is a number, so that line was a no-op (and throws under strict mode). With
    // Ymod.stat === 0 it had no visible effect, so it is intentionally dropped.

    while (temp.text.height > 220) {
      temp.text.style.fontSize--;
    }

    if (mode != "JustRender") {
      p.stage.addChild(temp);
      p.cards.push(temp);
    }
    return temp;
  }

  // ---------------------------------------------------------------------------
  // Rasterization helpers (generate -> HTMLImageElement)
  // ---------------------------------------------------------------------------

  // Downscale an HTMLImageElement while preserving aspect ratio.
  function ResizeImage(image, maxWidth, maxHeight) {
    const canvas = document.createElement("canvas");
    const scalingFactor = Math.min(
      maxWidth / image.width,
      maxHeight / image.height,
    );
    canvas.width = image.width * scalingFactor;
    canvas.height = image.height * scalingFactor;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    const resizedImage = new Image();
    resizedImage.src = canvas.toDataURL("image/png");
    return resizedImage;
  }

  // Ensure a card's base art asset is loaded before we rasterize it.
  async function ensureArtLoaded(card) {
    if (!card.selectedArt && arts[card.id + "_0"] && arts[card.id + "_0"].src) {
      try {
        await PIXI.Assets.load(arts[card.id + "_0"].src);
      } catch (err) {
        console.warn("[CardTileGenerator] base art preload failed:", err);
      }
    }
  }

  /**
   * Render a single card to an HTMLImageElement (transparent PNG).
   * @param {object} card
   * @param {object} [opts]
   * @param {number} [opts.scale=0.05] scale applied before extraction.
   * @param {number} [opts.maxSize=800] downscale if the extract exceeds this.
   */
  async function renderCardToImage(card, opts = {}) {
    return renderToImage(PixiCard, card, opts);
  }

  /** Render a single board tile to an HTMLImageElement. */
  async function renderTileToImage(card, opts = {}) {
    return renderToImage(PixiTile, card, opts);
  }

  async function renderToImage(builder, card, opts = {}) {
    if (!p) throw new Error("CardTileGenerator: call init() first.");
    const { scale = 0.05, maxSize = 800 } = opts;
    await ensureArtLoaded(card);
    const container = await builder(card, 0, 0, "JustRender");
    container.scale.set(scale, scale);
    let image = await p.renderer.extract.image(container);
    if (image.width > maxSize || image.height > maxSize) {
      image = ResizeImage(image, 470, 650);
    }
    container.destroy({ children: true });
    return image;
  }

  // ---------------------------------------------------------------------------
  // Demo overlay — renders a set of card ids into a floating preview panel.
  // Adapted from the original DemoPixiCard().
  // ---------------------------------------------------------------------------
  async function DemoPixiCard(...inputIds) {
    let ids = inputIds;
    if (ids.length === 1 && Array.isArray(ids[0])) ids = ids[0];
    if (!ids.length) ids = [1];
    ids = ids.map(Number).filter(Number.isFinite);
    if (!ids.length) ids = [1];

    if (!p || !p.renderer || !p.renderer.extract) {
      throw new Error("DemoPixiCard: call init() first.");
    }
    if (!Array.isArray(cards) || !cards.length) {
      throw new Error("DemoPixiCard: no cards[] supplied to init().");
    }

    document.getElementById("demoPixiCardPreviewGroup")?.remove();

    const group = document.createElement("div");
    group.id = "demoPixiCardPreviewGroup";
    group.style.cssText = `
      position: fixed; top: 16px; right: 16px; z-index: 999999;
      max-width: min(90vw, 1200px); max-height: calc(100vh - 32px);
      overflow: auto; background: rgba(0,0,0,0.92); border: 1px solid #666;
      border-radius: 10px; box-shadow: 0 8px 24px rgba(0,0,0,0.45); padding: 16px;`;

    const closeButton = document.createElement("button");
    closeButton.textContent = "×";
    closeButton.style.cssText = `
      position: sticky; top: 0; float: right; background: transparent;
      color: white; border: 0; font-size: 24px; line-height: 1; cursor: pointer;
      margin: 0 0 8px 8px;`;
    closeButton.onclick = () => group.remove();

    const title = document.createElement("div");
    title.textContent = "DemoPixiCard: " + ids.join(", ");
    title.style.cssText = "color:white;font:12px monospace;margin-bottom:12px;";

    const grid = document.createElement("div");
    grid.style.cssText = `
      display: grid; grid-template-columns: repeat(auto-fit, minmax(235px, 235px));
      gap: 12px; justify-content: start; align-items: start; clear: both;`;

    group.appendChild(closeButton);
    group.appendChild(title);
    group.appendChild(grid);
    document.body.appendChild(group);

    const results = [];
    for (const id of ids) {
      const source = cards.find((c) => Number(c.id) === id) || null;
      const slot = document.createElement("div");
      slot.style.cssText = `
        background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.12);
        border-radius: 8px; padding: 8px; min-height: 60px;`;

      if (!source) {
        const missing = document.createElement("div");
        missing.textContent = "Card id " + id + " not found";
        missing.style.cssText = "color:#ff9090;font:12px monospace;";
        slot.appendChild(missing);
        grid.appendChild(slot);
        continue;
      }

      // Clone so we never mutate the caller's card objects.
      const previewCard =
        typeof structuredClone === "function"
          ? structuredClone(source)
          : JSON.parse(JSON.stringify(source));
      if (
        !previewCard.selectedArt &&
        Array.isArray(previewCard.collectedArts) &&
        previewCard.collectedArts.length
      ) {
        previewCard.selectedArt = previewCard.collectedArts[0];
      }
      if (!arts[previewCard.id + "_0"]) registerArts([previewCard]);

      try {
        const image = await renderCardToImage(previewCard);
        const label = document.createElement("div");
        label.textContent =
          previewCard.id + " - " + (previewCard.name || "Unknown Card");
        label.style.cssText =
          "color:white;font:12px monospace;margin-bottom:8px;";
        image.style.cssText =
          "display:block;width:235px;height:auto;border-radius:4px;";
        slot.appendChild(label);
        slot.appendChild(image);
        grid.appendChild(slot);
        results.push({ id: previewCard.id, card: previewCard, image });
      } catch (renderError) {
        console.error("DemoPixiCard render failed for id:", id, renderError);
        const err = document.createElement("div");
        err.textContent = "Render failed for id " + id;
        err.style.cssText = "color:#ff9090;font:12px monospace;";
        slot.appendChild(err);
        grid.appendChild(slot);
      }
    }
    return { ids, results, element: group };
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------
  function buildApi() {
  return {
    // lifecycle
    init,
    registerArts,
    config,
    // generators
    PixiCard,
    PixiTile,
    // rasterization
    renderCardToImage,
    renderTileToImage,
    ResizeImage,
    // demo
    DemoPixiCard,
    // art / naming helpers
    SRC,
    CensorCard,
    RenderId,
    CardName,
    PixiGetArtTexture,
    LoadSpriteAsync,
    // text helpers / styles
    PixiText,
    TextStyleName,
    TextStyleTags,
    TextStyleRules,
    TextStyleFlavor,
    TextStyleCost,
    TextStyleGain,
    // expose state getters (read-only-ish) for debugging
    _state: () => ({ p, cards, arts, onlineImages, userData, duel }),
  };
  }

const CardTileGenerator = buildApi();
// Keep the browser global for demo.html + quick console poking.
if (typeof window !== "undefined") window.CardTileGenerator = CardTileGenerator;
export default CardTileGenerator;
